import React, { useState } from 'react'
import {
  ArrowDownTrayIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  PencilSquareIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline'
import {
  Badge,
  Button,
  CsvTableViewer,
  Input,
  Panel,
  PanelBody,
  PanelDescription,
  PanelHeader,
  PanelTitle,
  Textarea,
} from '../../components/ui'
import { cn } from '../../shared/lib/cn'
import {
  downloadVariableLagExport,
  openVariableLagReport,
  useVariableLagHealth,
  useVariableLagRun,
} from './api'
import type {
  AccrualCategory,
  EventItem,
  LegalItem,
  MarketingItem,
  TeLagItem,
  VariableLagHealth,
  VariableLagAccrualRun,
} from './types'
import { formatCurrency, formatPct, statusBadgeClass, varianceClass } from './utils'

type View = 'overview' | 'review' | 'sources' | 'exports'
type ReviewAction = 'approve' | 'modify' | 'reject'

interface ReviewDraft {
  action?: ReviewAction
  amount: string
  comment: string
  legalHours: string
  timestamp?: string
  error?: string
}

export default function VariableLagAccrualsPage({ view = 'overview' }: { view?: View }) {
  const { data, isLoading, error } = useVariableLagRun()
  const { data: health } = useVariableLagHealth()

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center text-sm text-gray-500 dark:text-gray-400">
        Loading variable lag accruals...
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="mx-auto max-w-[1180px] space-y-4">
        <DataSourceStrip health={health} />
        <Panel>
          <PanelBody>
            <p className="text-sm text-rose-600 dark:text-rose-400">
              Variable lag accrual data could not be loaded from Redshift.
            </p>
          </PanelBody>
        </Panel>
      </div>
    )
  }

  if (view === 'review') return <ReviewView run={data} health={health} />
  if (view === 'sources') return <SourcesView run={data} health={health} />
  if (view === 'exports') return <ExportsView run={data} health={health} />
  return <OverviewView run={data} health={health} />
}

function OverviewView({ run, health }: { run: VariableLagAccrualRun; health?: VariableLagHealth }) {
  return (
    <div className="mx-auto max-w-[1500px] space-y-6">
      <DataSourceStrip health={health} />
      <DecisionStrip run={run} />

      <div className="grid grid-cols-[minmax(0,1fr)_360px] gap-6 max-xl:grid-cols-1">
        <CategoryLedger categories={run.categories} />
        <ExceptionRail run={run} />
      </div>

      <div className="grid grid-cols-2 gap-6 max-2xl:grid-cols-1">
        <TeTable rows={run.details.te} />
        <LegalTable rows={run.details.legal} />
        <EventsTable rows={run.details.events} />
        <MarketingTable rows={run.details.marketing} />
      </div>
    </div>
  )
}

function DataSourceStrip({ health }: { health?: VariableLagHealth }) {
  if (!health) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 dark:border-dark-border dark:bg-dark-surface">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="info">Checking Redshift</Badge>
            <Badge variant="secondary">Resolving connector</Badge>
          </div>
          <Badge variant="info">Checking Langfuse</Badge>
        </div>
      </div>
    )
  }

  const redshiftVariant = health?.redshift_connected ? 'success' : 'danger'
  const langfuseVariant = health?.langfuse_enabled ? 'success' : 'warning'
  const modeLabel = health?.mode === 'saved'
    ? 'Saved connector'
    : health?.mode === 'env_fallback'
      ? 'Env fallback'
      : 'Unconfigured'

  return (
    <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 dark:border-dark-border dark:bg-dark-surface">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={redshiftVariant}>
            Redshift {health.redshift_connected ? 'connected' : 'not connected'}
          </Badge>
          <Badge variant="secondary">{modeLabel}</Badge>
          {health.redshift_schema && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              schema {health.redshift_schema}
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={langfuseVariant}>
            Langfuse {health.langfuse_enabled ? 'enabled' : 'inactive'}
          </Badge>
          {health.langfuse_project_id && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {health.langfuse_project_id}
            </span>
          )}
        </div>
      </div>
      {health.message && (
        <p className="mt-2 text-xs leading-5 text-gray-500 dark:text-gray-400">
          {health.message}
        </p>
      )}
    </div>
  )
}

function DecisionStrip({ run }: { run: VariableLagAccrualRun }) {
  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-dark-border dark:bg-dark-surface">
      <div className="grid grid-cols-[1.25fr_0.75fr_0.75fr_0.75fr] max-lg:grid-cols-2 max-sm:grid-cols-1">
        <div className="border-r border-gray-200 p-5 dark:border-dark-border max-sm:border-r-0">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Posting decision
          </div>
          <div className="mt-2 flex flex-wrap items-end gap-x-4 gap-y-2">
            <div className="text-3xl font-semibold tracking-normal text-charcoal dark:text-gray-100">
              {formatCurrency(run.summary.grand_total, true)}
            </div>
            <Badge variant="warning">{run.summary.hitl_required_count} controller actions</Badge>
          </div>
          <p className="mt-3 max-w-3xl text-sm leading-6 text-gray-600 dark:text-gray-300">
            {run.summary.alert}
          </p>
        </div>
        <DecisionMetric label="Threshold" value={formatCurrency(run.summary.total_threshold, true)} />
        <DecisionMetric label="Variance gate" value={`${Math.round(run.summary.variance_threshold * 100)}%`} />
        <DecisionMetric label="NetSuite files" value={run.summary.netsuite_ready ? 'Ready' : 'Blocked'} />
      </div>
    </div>
  )
}

function DecisionMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="border-r border-gray-200 p-5 last:border-r-0 dark:border-dark-border max-lg:border-t max-lg:last:border-r">
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {label}
      </div>
      <div className="mt-2 text-xl font-semibold text-charcoal dark:text-gray-100">{value}</div>
    </div>
  )
}

function CategoryLedger({ categories }: { categories: AccrualCategory[] }) {
  return (
    <Panel className="overflow-hidden">
      <PanelHeader>
        <PanelTitle>Category ledger</PanelTitle>
        <PanelDescription>
          Accrual, variance, posting accounts, and HITL disposition by line.
        </PanelDescription>
      </PanelHeader>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500 dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-400">
              <th className="px-5 py-3 font-semibold">Category</th>
              <th className="px-4 py-3 text-right font-semibold">Accrual</th>
              <th className="px-4 py-3 text-right font-semibold">3-mo avg</th>
              <th className="px-4 py-3 text-right font-semibold">Variance</th>
              <th className="px-4 py-3 font-semibold">Status</th>
              <th className="px-4 py-3 font-semibold">Posting</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-dark-border">
            {categories.map(category => (
              <tr key={category.id} className="align-top">
                <td className="px-5 py-4">
                  <div className="font-semibold text-charcoal dark:text-gray-100">
                    {category.label}
                  </div>
                  <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {category.detail_count} supporting rows
                  </div>
                </td>
                <td className="px-4 py-4 text-right font-semibold tabular-nums text-charcoal dark:text-gray-100">
                  {formatCurrency(category.amount)}
                </td>
                <td className="px-4 py-4 text-right tabular-nums text-gray-600 dark:text-gray-300">
                  {formatCurrency(category.historical_average)}
                </td>
                <td className={cn('px-4 py-4 text-right font-semibold tabular-nums', varianceClass(category.variance_pct))}>
                  {formatCurrency(category.variance_amount)}
                  <div className="text-xs">{formatPct(category.variance_pct)}</div>
                </td>
                <td className="px-4 py-4">
                  <span className={statusBadgeClass(category.status)}>
                    {category.status === 'hitl' ? 'HITL' : 'Auto'}
                  </span>
                  {category.hitl_reason && (
                    <p className="mt-2 max-w-[280px] text-xs leading-5 text-gray-500 dark:text-gray-400">
                      {category.hitl_reason}
                    </p>
                  )}
                </td>
                <td className="px-4 py-4 text-xs leading-5 text-gray-500 dark:text-gray-400">
                  <div>DR {category.debit_account}</div>
                  <div>CR {category.credit_account}</div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  )
}

function ExceptionRail({ run }: { run: VariableLagAccrualRun }) {
  const events = run.categories.find(category => category.id === 'events')
  const marketing = run.categories.find(category => category.id === 'marketing')
  return (
    <div className="space-y-4">
      <Panel>
        <PanelHeader>
          <PanelTitle className="text-lg">Controller focus</PanelTitle>
          <PanelDescription>Items most likely to change posting readiness.</PanelDescription>
        </PanelHeader>
        <PanelBody className="space-y-5">
          <FocusItem label="GC hours" value={`${run.details.legal.length} matters`} detail="Legal always requires confirmation." />
          <FocusItem label="Large event" value={events?.has_single_large_item ? 'Present' : 'Clear'} detail="Cavallo Point offsite exceeds the $5K event gate." />
          <FocusItem label="Marketing variance" value={marketing ? formatPct(marketing.variance_pct) : '-'} detail="Campaign ramp is outside the variance threshold." />
        </PanelBody>
      </Panel>

      <Panel>
        <PanelBody className="space-y-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Card source check
          </div>
          <div className="flex items-baseline justify-between gap-4">
            <span className="text-2xl font-semibold text-charcoal dark:text-gray-100">
              {run.card_evidence.receipt_exception_count}
            </span>
            <span className="text-sm text-gray-500 dark:text-gray-400">receipt exceptions</span>
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-300">
            {run.card_evidence.transaction_count} card transactions totaling {formatCurrency(run.card_evidence.statement_total)}.
          </div>
        </PanelBody>
      </Panel>
    </div>
  )
}

function FocusItem({ label, value, detail }: { label: string; value: string; detail: string }) {
  return (
    <div className="border-l-2 border-gray-200 pl-4 dark:border-dark-border">
      <div className="flex items-baseline justify-between gap-4">
        <div className="text-sm font-medium text-charcoal dark:text-gray-100">{label}</div>
        <div className="text-sm font-semibold text-charcoal dark:text-gray-100">{value}</div>
      </div>
      <p className="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">{detail}</p>
    </div>
  )
}

function ReviewView({ run, health }: { run: VariableLagAccrualRun; health?: VariableLagHealth }) {
  const [drafts, setDrafts] = useState<Record<string, ReviewDraft>>({})
  const completed = run.categories.filter(category => drafts[category.id]?.action).length
  const categoryCount = run.categories.length

  const getDraft = (category: AccrualCategory): ReviewDraft =>
    drafts[category.id] ?? {
      amount: String(Math.round(category.amount)),
      comment: '',
      legalHours: '',
    }

  const updateDraft = (category: AccrualCategory, patch: Partial<ReviewDraft>) => {
    const current = getDraft(category)
    setDrafts(prev => ({
      ...prev,
      [category.id]: { ...current, ...patch, error: undefined },
    }))
  }

  const submit = (category: AccrualCategory, action: ReviewAction) => {
    const draft = getDraft(category)
    if ((action === 'reject' || action === 'modify') && !draft.comment.trim()) {
      updateDraft(category, { error: 'Comment required for this action.' })
      return
    }
    if (category.id === 'legal' && action !== 'reject' && !draft.legalHours.trim()) {
      updateDraft(category, { error: 'GC-confirmed hours are required.' })
      return
    }
    updateDraft(category, {
      action,
      timestamp: new Date().toLocaleString(),
      error: undefined,
    })
  }

  return (
    <div className="mx-auto max-w-[1380px] space-y-6">
      <DataSourceStrip health={health} />
      <div className="grid grid-cols-[1fr_320px] gap-6 max-xl:grid-cols-1">
        <Panel>
          <PanelHeader>
            <PanelTitle>HITL action queue</PanelTitle>
            <PanelDescription>
              Approve, modify, or reject each category before NetSuite upload.
            </PanelDescription>
          </PanelHeader>
          <PanelBody>
            <div className="grid grid-cols-2 gap-4 max-lg:grid-cols-1">
              {run.categories.map(category => {
                const draft = getDraft(category)
                return (
                  <div
                    key={category.id}
                    className={cn(
                      'rounded-lg border p-4',
                      draft.action
                        ? 'border-emerald-200 bg-emerald-50/50 dark:border-emerald-900 dark:bg-emerald-950/10'
                        : 'border-gray-200 bg-white dark:border-dark-border dark:bg-dark-surface',
                    )}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="font-semibold text-charcoal dark:text-gray-100">{category.label}</h3>
                        <p className="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">
                          {category.hitl_reason || 'Within auto-approve guardrails.'}
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold tabular-nums text-charcoal dark:text-gray-100">
                          {formatCurrency(category.amount)}
                        </div>
                        <div className={cn('text-xs font-semibold', varianceClass(category.variance_pct))}>
                          {formatPct(category.variance_pct)}
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-3">
                      {category.id === 'legal' && (
                        <label className="col-span-2 text-xs font-medium text-gray-600 dark:text-gray-300">
                          GC-confirmed hours
                          <Input
                            className="mt-1"
                            type="number"
                            value={draft.legalHours}
                            onChange={event => updateDraft(category, { legalHours: event.target.value })}
                            placeholder="Enter hours"
                          />
                        </label>
                      )}
                      <label className="text-xs font-medium text-gray-600 dark:text-gray-300">
                        Adjusted amount
                        <Input
                          className="mt-1"
                          type="number"
                          value={draft.amount}
                          onChange={event => updateDraft(category, { amount: event.target.value })}
                        />
                      </label>
                      <label className="text-xs font-medium text-gray-600 dark:text-gray-300">
                        Status
                        <div className="mt-1 flex h-10 items-center rounded-md border border-gray-200 px-3 dark:border-dark-border">
                          <span className={statusBadgeClass(category.status)}>
                            {category.status === 'hitl' ? 'HITL' : 'Auto'}
                          </span>
                        </div>
                      </label>
                      <label className="col-span-2 text-xs font-medium text-gray-600 dark:text-gray-300">
                        Controller comment
                        <Textarea
                          className="mt-1 min-h-[72px]"
                          value={draft.comment}
                          onChange={event => updateDraft(category, { comment: event.target.value })}
                          placeholder="Record support, approval basis, or rejection reason."
                        />
                      </label>
                    </div>

                    {draft.error && (
                      <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700 dark:border-rose-900 dark:bg-rose-950/30 dark:text-rose-300">
                        {draft.error}
                      </div>
                    )}
                    {draft.action && (
                      <div className="mt-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-300">
                        {actionLabel(draft.action)} at {draft.timestamp}
                      </div>
                    )}

                    <div className="mt-4 flex flex-wrap gap-2">
                      <Button size="sm" variant="secondary" onClick={() => submit(category, 'approve')}>
                        <CheckCircleIcon />
                        Approve
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => submit(category, 'modify')}>
                        <PencilSquareIcon />
                        Modify
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => submit(category, 'reject')}>
                        <XCircleIcon />
                        Reject
                      </Button>
                    </div>
                  </div>
                )
              })}
            </div>
          </PanelBody>
        </Panel>

        <Panel className="self-start">
          <PanelHeader>
            <PanelTitle className="text-lg">Completion</PanelTitle>
            <PanelDescription>Session-local controller actions.</PanelDescription>
          </PanelHeader>
          <PanelBody className="space-y-4">
            <div>
              <div className="flex items-baseline justify-between">
                <span className="text-3xl font-semibold text-charcoal dark:text-gray-100">
                  {completed}/{categoryCount}
                </span>
                <span className="text-sm text-gray-500 dark:text-gray-400">actions</span>
              </div>
              <div className="mt-3 h-2 overflow-hidden rounded-full bg-gray-100 dark:bg-dark-surface-2">
                <div
                  className="h-full rounded-full bg-eliza-red transition-[width] duration-300"
                  style={{ width: `${categoryCount ? (completed / categoryCount) * 100 : 0}%` }}
                />
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 p-3 text-sm leading-6 text-gray-600 dark:border-dark-border dark:text-gray-300">
              {completed === categoryCount
                ? 'All categories have controller action. Export files are ready for upload.'
                : 'Complete all category actions before posting the prepared NetSuite files.'}
            </div>
          </PanelBody>
        </Panel>
      </div>
    </div>
  )
}

function SourcesView({ run, health }: { run: VariableLagAccrualRun; health?: VariableLagHealth }) {
  return (
    <div className="mx-auto grid max-w-[1360px] grid-cols-[minmax(0,1fr)_420px] gap-6 max-xl:grid-cols-1">
      <div className="xl:col-span-2">
        <DataSourceStrip health={health} />
      </div>
      <Panel>
        <PanelHeader>
          <PanelTitle>Source files</PanelTitle>
          <PanelDescription>Repo-owned copies of the original Variable Lag Accruals bundle.</PanelDescription>
        </PanelHeader>
        <PanelBody className="space-y-3">
          {run.source_files.map(file => (
            <div
              key={file.filename}
              className="grid grid-cols-[220px_minmax(0,1fr)_80px] gap-4 rounded-lg border border-gray-200 px-4 py-3 text-sm dark:border-dark-border max-md:grid-cols-1"
            >
              <div className="min-w-0 break-words font-medium text-charcoal dark:text-gray-100">{file.filename}</div>
              <div className="min-w-0 break-words text-gray-500 dark:text-gray-400">{file.description}</div>
              <div className="text-right max-md:text-left">
                <Badge variant={file.available ? 'success' : 'danger'}>
                  {file.available ? 'Available' : 'Missing'}
                </Badge>
              </div>
            </div>
          ))}
          <Button variant="secondary" onClick={() => openVariableLagReport()}>
            <DocumentTextIcon />
            Open source HTML report
          </Button>
        </PanelBody>
      </Panel>

      <Panel className="self-start">
        <PanelHeader>
          <PanelTitle className="text-lg">Card evidence</PanelTitle>
          <PanelDescription>Corporate card statement cross-check.</PanelDescription>
        </PanelHeader>
        <PanelBody className="space-y-4">
          <FocusItem label="Transactions" value={`${run.card_evidence.transaction_count}`} detail="Rows parsed from Corporate_Card_Statement." />
          <FocusItem label="Statement total" value={formatCurrency(run.card_evidence.statement_total)} detail="Gross card activity in the source workbook." />
          <FocusItem label="Receipt exceptions" value={`${run.card_evidence.receipt_exception_count}`} detail="Rows not marked as fully receipted." />
        </PanelBody>
      </Panel>

      <Panel className="xl:col-span-2">
        <PanelHeader>
          <PanelTitle>Receipt exceptions</PanelTitle>
          <PanelDescription>Exceptions surfaced from the supplemental card workbook.</PanelDescription>
        </PanelHeader>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="border-b border-gray-200 bg-gray-50 text-xs uppercase tracking-wide text-gray-500 dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-400">
              <tr>
                <th className="px-5 py-3 text-left font-semibold">Cardholder</th>
                <th className="px-4 py-3 text-left font-semibold">Merchant</th>
                <th className="px-4 py-3 text-left font-semibold">Dept</th>
                <th className="px-4 py-3 text-right font-semibold">Amount</th>
                <th className="px-4 py-3 text-left font-semibold">Receipt</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-dark-border">
              {run.card_evidence.receipt_exceptions.map((row, index) => (
                <tr key={`${row.cardholder}-${row.merchant}-${index}`}>
                  <td className="px-5 py-3 font-medium text-charcoal dark:text-gray-100">{row.cardholder}</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{row.merchant}</td>
                  <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{row.department}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{formatCurrency(row.amount)}</td>
                  <td className="px-4 py-3"><Badge variant="warning">{row.receipt || 'Open'}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  )
}

function ExportsView({ run, health }: { run: VariableLagAccrualRun; health?: VariableLagHealth }) {
  const [busy, setBusy] = useState<string | null>(null)
  const exports = [
    { type: 'je' as const, label: 'Journal entry load', data: run.exports.journal_entry },
    { type: 'reversal' as const, label: 'Auto-reversal load', data: run.exports.reversal },
  ]

  const handleDownload = async (type: 'je' | 'reversal') => {
    setBusy(type)
    try {
      await downloadVariableLagExport(type)
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="mx-auto max-w-[1180px] space-y-6">
      <DataSourceStrip health={health} />
      <Panel>
        <PanelHeader>
          <PanelTitle>NetSuite export readiness</PanelTitle>
          <PanelDescription>
            Both prepared files are validated for required DR/CR balance before download.
          </PanelDescription>
        </PanelHeader>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="border-b border-gray-200 bg-gray-50 text-xs uppercase tracking-wide text-gray-500 dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-400">
              <tr>
                <th className="px-5 py-3 text-left font-semibold">File</th>
                <th className="px-4 py-3 text-right font-semibold">Rows</th>
                <th className="px-4 py-3 text-right font-semibold">Debits</th>
                <th className="px-4 py-3 text-right font-semibold">Credits</th>
                <th className="px-4 py-3 text-left font-semibold">Status</th>
                <th className="px-4 py-3 text-right font-semibold">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-dark-border">
              {exports.map(item => (
                <tr key={item.type}>
                  <td className="px-5 py-4">
                    <div className="font-semibold text-charcoal dark:text-gray-100">{item.label}</div>
                    <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">{item.data.filename}</div>
                  </td>
                  <td className="px-4 py-4 text-right tabular-nums">{item.data.rows}</td>
                  <td className="px-4 py-4 text-right tabular-nums">{formatCurrency(item.data.debit_total)}</td>
                  <td className="px-4 py-4 text-right tabular-nums">{formatCurrency(item.data.credit_total)}</td>
                  <td className="px-4 py-4">
                    <Badge variant={item.data.balanced ? 'success' : 'danger'}>
                      {item.data.balanced ? 'Balanced' : 'Out of balance'}
                    </Badge>
                  </td>
                  <td className="px-4 py-4 text-right">
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={busy === item.type}
                      onClick={() => handleDownload(item.type)}
                    >
                      <ArrowDownTrayIcon />
                      {busy === item.type ? 'Preparing' : 'Download'}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      <div className="grid grid-cols-3 gap-4 max-lg:grid-cols-1">
        <ExportNote title="Auto-reverse" body="The April journal file carries the May 1 auto-reversal flag." />
        <ExportNote title="Source lineage" body="Each line cites the source workbook and source tab used for the calculation." />
        <ExportNote title="Upload gate" body="Controller HITL review should be complete before NetSuite import." />
      </div>
    </div>
  )
}

function ExportNote({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-dark-border dark:bg-dark-surface">
      <div className="font-semibold text-charcoal dark:text-gray-100">{title}</div>
      <p className="mt-2 text-sm leading-6 text-gray-500 dark:text-gray-400">{body}</p>
    </div>
  )
}

function TeTable({ rows }: { rows: TeLagItem[] }) {
  return (
    <DetailPanel title="T&E lag" description="Unsubmitted card balances with receipt exceptions surfaced.">
      <CsvTableViewer
        filename="te-lag-accruals.csv"
        pageSize={8}
        columns={[
          { id: 'employee', header: 'Employee', minWidth: '160px' },
          { id: 'department', header: 'Dept', minWidth: '150px' },
          { id: 'days', header: 'Days', align: 'right', width: '80px' },
          { id: 'receipt', header: 'Receipt', width: '110px' },
          { id: 'accrual', header: 'Accrual', align: 'right', minWidth: '120px' },
        ]}
        rows={rows.map(row => ({
          employee: row.employee,
          department: row.department,
          days: row.days_outstanding,
          receipt: row.receipt_status,
          accrual: formatCurrency(row.accrual_amount),
        }))}
        cellClassName={(value, _row, column) =>
          column.id === 'receipt' && !['Yes', 'Clear'].includes(String(value))
            ? 'font-semibold text-amber-700 dark:text-amber-300'
            : undefined
        }
      />
    </DetailPanel>
  )
}

function LegalTable({ rows }: { rows: LegalItem[] }) {
  return (
    <DetailPanel title="Legal services" description="Open matters requiring GC-confirmed hour support.">
      <CsvTableViewer
        filename="legal-services-accruals.csv"
        pageSize={8}
        columns={[
          { id: 'matter', header: 'Matter', minWidth: '120px' },
          { id: 'firm', header: 'Firm', minWidth: '180px' },
          { id: 'hours', header: 'Hours', align: 'right', width: '90px' },
          { id: 'rate', header: 'Rate', align: 'right', minWidth: '110px' },
          { id: 'accrual', header: 'Accrual', align: 'right', minWidth: '120px' },
        ]}
        rows={rows.map(row => ({
          matter: row.matter_id,
          firm: row.firm,
          hours: row.hours_estimate,
          rate: row.rate,
          accrual: formatCurrency(row.accrual_amount),
        }))}
      />
    </DetailPanel>
  )
}

function EventsTable({ rows }: { rows: EventItem[] }) {
  return (
    <DetailPanel title="Events and meals" description="Unposted event spend filtered where In GL is No.">
      <CsvTableViewer
        filename="events-meals-accruals.csv"
        pageSize={8}
        columns={[
          { id: 'date', header: 'Date', minWidth: '110px' },
          { id: 'event', header: 'Event', minWidth: '190px' },
          { id: 'vendor', header: 'Vendor', minWidth: '170px' },
          { id: 'flag', header: 'Flag', minWidth: '150px' },
          { id: 'accrual', header: 'Accrual', align: 'right', minWidth: '120px' },
        ]}
        rows={rows.map(row => ({
          date: row.date,
          event: row.event,
          vendor: row.vendor,
          flag: row.flag,
          accrual: formatCurrency(row.accrual_amount),
        }))}
        cellClassName={(value, _row, column) =>
          column.id === 'flag' && !['Yes', 'Clear'].includes(String(value))
            ? 'font-semibold text-amber-700 dark:text-amber-300'
            : undefined
        }
      />
    </DetailPanel>
  )
}

function MarketingTable({ rows }: { rows: MarketingItem[] }) {
  return (
    <DetailPanel title="Marketing lag" description="Committed programs in flight with lag accrual.">
      <CsvTableViewer
        filename="marketing-lag-accruals.csv"
        pageSize={8}
        columns={[
          { id: 'program', header: 'Program', minWidth: '220px' },
          { id: 'vendor', header: 'Vendor', minWidth: '170px' },
          { id: 'lag', header: 'Lag', minWidth: '100px' },
          { id: 'account', header: 'Account', align: 'right', minWidth: '100px' },
          { id: 'accrual', header: 'Accrual', align: 'right', minWidth: '120px' },
        ]}
        rows={rows.map(row => ({
          program: row.program,
          vendor: row.vendor,
          lag: row.invoice_lag_estimate,
          account: row.account_number,
          accrual: formatCurrency(row.accrual_amount),
        }))}
      />
    </DetailPanel>
  )
}

function DetailPanel({
  title,
  description,
  children,
}: {
  title: string
  description: string
  children: React.ReactNode
}) {
  return (
    <Panel className="overflow-hidden">
      <PanelHeader>
        <PanelTitle className="text-lg">{title}</PanelTitle>
        <PanelDescription>{description}</PanelDescription>
      </PanelHeader>
      {children}
    </Panel>
  )
}

function actionLabel(action: ReviewAction) {
  if (action === 'approve') return 'Approved'
  if (action === 'modify') return 'Modified'
  return 'Rejected'
}
